import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ChildrenStoreData, EyengagePost, TagsData } from "src/shared/types/src";
import LikeCommentFooter from "../../components/LikeCommentFooter";
import { ENTITY_TYPE, EYENGAGE_POST_TYPES } from "src/shared/config/src";
import LocalImages from './../../static';
import PostDescriptionComponent from "./components/PostDescriptionComponent";
import PostPollContainer from "./components/PostPollContainer";
import ConfirmPostComponent from "./components/ConfirmPostComponent";
import { useAppDispatch, useAppSelector } from "../../services/store";
import { EyengageActions } from "src/shared/redux-state/src/lib/eyengage/EyengageSlice";
import { fetchEyengagePostDetails } from "src/shared/services/src";
import { getCurrentChild } from "src/shared/redux-state/src";
import { AxiosError } from "axios";
import { LikeFooterLoader, MediaCollageContainer, MediaFiveLoader, PostAndObservationHeader, TagListRenderer } from "src/app/components";
import BackButtonLayout from "src/app/layouts/BackButtonLayout";
import ProfileLayout from "src/app/layouts/ProfileLayout";
import EventDetails from "./components/EventDetails";

export default function EyengagePostDetailsScreen() {
    const dispatch = useAppDispatch();
    const { postId } = useParams();

    const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);

    const [postDetailLoading, setPostDetailLoading] = useState<boolean>(false);
    const [postDetails, setPostDetails] = useState<EyengagePost | null>(null);
    const [hasPostDetailError, setPostDetailError] = useState<boolean>(false);
    const [postDetailsErrorMsg, setPostDetailErrorMsg] = useState<string>('');

    const fetchPostDetails = useCallback(async (id: string) => {
        try {
            setPostDetailLoading(true);
            const response = await fetchEyengagePostDetails({
                child: currentChild,
                postId: id
            });

            if (response && Object.keys(response).length) {
                setPostDetails(response.post);
            } else {
                setPostDetails(null);
            }
        } catch (error) {
            console.error("Error fetching post details:", error);
            if (error instanceof AxiosError && error.response?.data?.errors) {
                const { errors } = error.response.data;
                setPostDetailError(true);
                setPostDetailErrorMsg(Array.isArray(errors) ? errors.join(',') : errors);
            }
        } finally {
            setPostDetailLoading(false);
        }
    }, [currentChild]);

    useEffect(() => {
        if (postId) {
            fetchPostDetails(postId);
        }
    }, [postId, fetchPostDetails]);

    const handleLikeUnlike = useCallback(async ({ likeCount, likedBySelf }: { likeCount: number, likedBySelf: boolean }) => {
        if (!postDetails) return;

        const updatedPostDetails: EyengagePost = {
            ...postDetails,
            likes: { ...postDetails.likes, count: likeCount, self: likedBySelf }
        };
        setPostDetails(updatedPostDetails);
        await dispatch(EyengageActions.updateLikeCountInPostById({
            postId: postDetails.id,
            updatedLikeCount: likeCount,
            likedBySelf
        }));
    }, [postDetails, dispatch]);

    const handleAddComment = useCallback(async () => {
        if (!postDetails) return;

        const updatedCommentCount = (postDetails?.comments?.count ?? 0) + 1;
        const updatedPostDetails: EyengagePost = {
            ...postDetails,
            comments: { ...postDetails.comments, count: updatedCommentCount }
        };
        setPostDetails(updatedPostDetails);
        await dispatch(EyengageActions.updateCommentCountInPostById({
            postId: postDetails.id,
            updatedCommentCount
        }));
    }, [postDetails, dispatch]);

    const handleDeleteComment = useCallback(async () => {
        if (!postDetails) return;

        const updatedCommentCount = (postDetails?.comments?.count ?? 0) - 1;
        const updatedPostDetails: EyengagePost = {
            ...postDetails,
            comments: {
                ...postDetails.comments,
                count: updatedCommentCount
            }
        };
        setPostDetails(updatedPostDetails);
    }, [postDetails]);

    if (postDetailLoading) {
        return <BackButtonLayout fixedFooter={true}>
            <MediaFiveLoader />
            <div className="like-comment-footer">
                <LikeFooterLoader />
            </div>
        </BackButtonLayout>;
    }

    if (hasPostDetailError || !postDetails) {
        return (
            <BackButtonLayout>
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: 'calc(100vh - 124px)' }}>
                    <img src={LocalImages.noPostFound} alt="eyengage-empty" style={{ width: '120px' }} />
                    <p className="mg-b-0 fs-14 text-black fw-semibold mt-2">{postDetailsErrorMsg || "Post not found"}</p>
                </div>
            </BackButtonLayout>
        );
    }

    return (
        <ProfileLayout title={"Post Details"} fixedFooter={true}>

            <PostAndObservationHeader
                name={postDetails.addedBy.name}
                photo={postDetails.addedBy.photo}
                date={postDetails.date}
            />

            <TagListRenderer
                tags={[{
                    color: postDetails.category.color,
                    label: postDetails.category.name
                } as TagsData]}
            />

            {(postDetails.type === EYENGAGE_POST_TYPES.EVENT) && <EventDetails postData={postDetails} />}

            <PostDescriptionComponent
                showFullText={true}
                postData={postDetails}
                descriptionText={postDetails.description}
            />

            {(postDetails?.poll?.options?.length > 0) && (
                <PostPollContainer postData={postDetails} />
            )}

            {postDetails.attachments?.length > 0 && (
                <MediaCollageContainer
                    media={postDetails.attachments}
                    openCarousel={true}
                />
            )}

            {postDetails.confirmationRequired && (
                <ConfirmPostComponent
                    confirmedAt={postDetails.confirmedAt}
                    confirmationStatus={postDetails.confirmedByMe}
                    postId={postDetails.id}
                />
            )}

            {!postDetails.disableReactions && (
                <div className="like-comment-footer">
                    <LikeCommentFooter
                        likedByMe={postDetails.likes.self}
                        likeCount={postDetails.likes.count || 0}
                        commentCount={postDetails.comments.count || 0}
                        entityId={postDetails.id}
                        entityType={ENTITY_TYPE.EYENGAGE}
                        onLikeUnlike={handleLikeUnlike}
                        onAddComment={handleAddComment}
                        onDeleteComment={handleDeleteComment}
                    />
                </div>
            )}
        </ProfileLayout>

    );
}
